'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import useUserStore from '@/state/userState';
import useOrganizationStore from '@/state/orgState';
import { SettingsTabProps } from '@/types/settings';
import { removeMemberFromOrganizationRequest } from '@/apis/removeMemberFromOrganization';
import { useRouter } from 'next/navigation';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isOwner, roleLabel } from '@/lib/rbac';
import { editUser } from '@/apis/ApiUtil';
import { useToast } from '@/hooks/useToast';
import LinkedOrgsCard, {
  type LinkedOrgInfo,
} from '@/components/Settings/LinkedOrgsCard';
import { SettingsField } from '@/components/Settings/SettingsField';
import { useProviderDisplayName } from '@/utils/getProviderDisplayName';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useQueryClient } from '@tanstack/react-query';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  setLeaderboardHidden as setLeaderboardHiddenApi,
  LEADERBOARD_HIDDEN_FLAG,
} from '@/apis/featureFlags';

type ProfileResult = {
  name: string | null;
  avatarUrl: string | null;
  email: string | null;
};

async function fetchProfileFromGitHub(
  username: string,
  accessToken?: string
): Promise<ProfileResult> {
  try {
    const headers: HeadersInit = { Accept: 'application/json' };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      headers['User-Agent'] = 'Entelligence-App';
    }
    const res = await fetch(
      `https://api.github.com/users/${encodeURIComponent(username)}`,
      { headers }
    );
    if (!res.ok) return { name: null, avatarUrl: null, email: null };
    const data = (await res.json()) as {
      name?: string | null;
      avatar_url?: string | null;
      email?: string | null;
    };
    const name = typeof data?.name === 'string' ? data.name.trim() : null;
    const avatarUrl =
      typeof data?.avatar_url === 'string' ? data.avatar_url.trim() : null;
    const email = typeof data?.email === 'string' ? data.email.trim() : null;
    return {
      name: name || null,
      avatarUrl: avatarUrl || null,
      email: email || null,
    };
  } catch {
    return { name: null, avatarUrl: null, email: null };
  }
}

async function fetchProfileFromGitLab(
  username: string
): Promise<ProfileResult> {
  try {
    const res = await fetch(
      `https://gitlab.com/api/v4/users?username=${encodeURIComponent(username)}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!res.ok) return { name: null, avatarUrl: null, email: null };
    const data = (await res.json()) as Array<{
      name?: string | null;
      avatar_url?: string | null;
      email?: string | null;
    }>;
    const first = Array.isArray(data) ? data[0] : undefined;
    if (first == null || typeof first !== 'object')
      return { name: null, avatarUrl: null, email: null };
    const raw = first as {
      name?: string | null;
      avatar_url?: string | null;
      email?: string | null;
    };
    const name = typeof raw.name === 'string' ? raw.name.trim() : null;
    const avatarUrl =
      typeof raw.avatar_url === 'string' ? raw.avatar_url.trim() : null;
    const email = typeof raw.email === 'string' ? raw.email.trim() : null;
    return {
      name: name || null,
      avatarUrl: avatarUrl || null,
      email: email || null,
    };
  } catch {
    return { name: null, avatarUrl: null, email: null };
  }
}

function GeneralTabSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl font-semibold text-ds-text-darker">{children}</h2>
  );
}

/** A linked org's name + UUID, matching the two SettingsField cells the
 * home org already renders above it in the same grid. */
function SettingsFieldPair({
  orgName,
  orgUuid,
}: {
  orgName: string;
  orgUuid: string;
}) {
  return (
    <>
      <SettingsField label="Org name" value={orgName} />
      <SettingsField label="Org ID" value={orgUuid} mono />
    </>
  );
}

export default function GeneralTab({ className }: SettingsTabProps) {
  const user = useUserStore((state) => state.user);
  const setPureUser = useUserStore((state) => state.setPureUser);
  const organization = useOrganizationStore((state) => state.organization);
  const router = useRouter();
  const { user: loggedUser } = useUserLogged();
  const toast = useToast();
  const [isLeaving, setIsLeaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: [user?.FirstName, user?.LastName].filter(Boolean).join(' '),
  });
  const hasTriedFetchingProfile = useRef(false);

  // ENT-3531: admin-only org-wide leaderboard hide toggle.
  const isAdmin = isOwner(user?.Role);
  const providerDisplayName = useProviderDisplayName();
  const orgUuid = organization?.OrgUUID ?? '';
  const queryClient = useQueryClient();
  const { isEnabled: isFeatureEnabled, isLoading: featureFlagsLoading } =
    useFeatureFlags(orgUuid);
  const currentLeaderboardHidden = isFeatureEnabled(LEADERBOARD_HIDDEN_FLAG);
  const [leaderboardHidden, setLeaderboardHidden] = useState(false);
  const [togglingLeaderboard, setTogglingLeaderboard] = useState(false);
  const [linkedOrgs, setLinkedOrgs] = useState<LinkedOrgInfo[]>([]);

  // Keep the local switch in sync with the fetched flag (primitive dep — no loop).
  useEffect(() => {
    setLeaderboardHidden(currentLeaderboardHidden);
  }, [currentLeaderboardHidden]);

  const handleLeaderboardHiddenToggle = async (checked: boolean) => {
    if (!orgUuid) return;
    setTogglingLeaderboard(true);
    setLeaderboardHidden(checked); // optimistic
    const res = await setLeaderboardHiddenApi(orgUuid, checked);
    if (res.Error) {
      console.error('[Leaderboard Hidden] update failed:', res.Error);
      setLeaderboardHidden(!checked); // rollback
      toast({
        title: 'Something went wrong',
        description:
          'Couldn’t update the leaderboard setting. Please try again in a moment.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ['featureFlags', orgUuid] });
      toast({
        title: 'Saved',
        description: checked
          ? 'The leaderboard is now hidden from your organization.'
          : 'The leaderboard is now visible to your organization.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
    setTogglingLeaderboard(false);
  };

  // When full name, profile image, or email is empty, try to fetch from GitHub/GitLab and persist
  useEffect(() => {
    if (!user?.UserUUID || hasTriedFetchingProfile.current) return;
    const fullName = [user.FirstName, user.LastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const imageEmpty = !user.Image?.trim();
    const nameEmpty = fullName === '';
    const emailEmpty = !user.Email?.trim();
    const canFetch =
      (user.GithubUsername || user.GitlabUsername) &&
      (nameEmpty || imageEmpty || emailEmpty);
    if (!canFetch) return;

    hasTriedFetchingProfile.current = true;
    const provider = loggedUser?.provider;
    const accessToken = loggedUser?.accessToken;

    const run = async () => {
      try {
        let profile: ProfileResult = {
          name: null,
          avatarUrl: null,
          email: null,
        };
        if (provider === 'github' && user.GithubUsername) {
          profile = await fetchProfileFromGitHub(
            user.GithubUsername,
            accessToken
          );
        } else if (
          (provider === 'gitlab' || !provider) &&
          user.GitlabUsername
        ) {
          profile = await fetchProfileFromGitLab(user.GitlabUsername);
        }
        if (
          !provider &&
          user.GithubUsername &&
          !profile.name &&
          !profile.avatarUrl &&
          !profile.email
        ) {
          profile = await fetchProfileFromGitHub(
            user.GithubUsername,
            accessToken
          );
        }

        const firstName =
          nameEmpty && profile.name
            ? profile.name.split(' ')[0] || ''
            : user.FirstName || '';
        const lastName =
          nameEmpty && profile.name
            ? profile.name.split(' ').slice(1).join(' ') || ''
            : user.LastName || '';
        const imageToSave =
          imageEmpty && profile.avatarUrl ? profile.avatarUrl : undefined;
        // Only use fetched email when user's email is empty; never overwrite existing email
        const emailToSave =
          emailEmpty && profile.email ? profile.email : undefined;

        const shouldUpdate =
          (nameEmpty && profile.name) ||
          (imageEmpty && profile.avatarUrl) ||
          (emailEmpty && profile.email);
        if (!shouldUpdate) return;

        const result = await editUser(
          user.UserUUID,
          firstName,
          lastName,
          user.Role || 'member',
          imageToSave,
          emailToSave
        );
        if (!result?.Error && user) {
          setPureUser({
            ...user,
            FirstName: firstName,
            LastName: lastName,
            ...(imageToSave ? { Image: imageToSave } : {}),
            ...(emailToSave ? { Email: emailToSave } : {}),
          });
        }
      } catch {
        // Ignore errors: fetch or editUser failures must not affect the page
      }
    };
    run();
  }, [user, loggedUser?.provider, loggedUser?.accessToken, setPureUser]);

  const handleLeaveOrg = async () => {
    if (!user?.UserUUID || !organization?.OrgUUID) {
      console.error('Missing user or organization data');
      return;
    }

    setIsLeaving(true);

    // Start the API call but don't wait for it
    removeMemberFromOrganizationRequest(
      {
        UserUUID: user.UserUUID,
        OrgUUID: organization.OrgUUID,
      },
      { Authorization: `Bearer ${loggedUser?.accessToken}` }
    ).catch((error) => {
      console.error('Error leaving organization:', error);
      // Log error but don't block the redirect
    });

    // Add a small delay to show loading state, then redirect
    setTimeout(() => {
      router.push('/');
    }, 1500);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setFormData({
      fullName: [user?.FirstName, user?.LastName].filter(Boolean).join(' '),
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      fullName: [user?.FirstName, user?.LastName].filter(Boolean).join(' '),
    });
  };

  const handleSave = async () => {
    if (!user?.UserUUID) {
      toast({
        title: 'Error',
        description: 'User information is missing',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);

    try {
      // Split full name into first and last name
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const result = await editUser(
        user.UserUUID,
        firstName,
        lastName,
        user.Role || 'member'
      );

      if (result.Error) {
        toast({
          title: 'Error',
          description: 'Failed to update user information',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Update local state
        setPureUser({
          ...user,
          FirstName: firstName,
          LastName: lastName,
        });

        toast({
          title: 'Success',
          description: 'Your name has been updated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = () => {
    const fullName = [user?.FirstName, user?.LastName]
      .filter(Boolean)
      .join(' ');
    const nameParts = fullName.split(' ').filter(Boolean);
    if (nameParts.length >= 2) {
      return (
        nameParts[0].charAt(0).toUpperCase() +
        nameParts[nameParts.length - 1].charAt(0).toUpperCase()
      );
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className={`space-y-8 motion-stagger ${className || ''}`}>
      {/* Your Details Section */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <GeneralTabSectionTitle>Your Details</GeneralTabSectionTitle>
          {!isEditing ? (
            <Button onClick={handleEdit} variant="outline" className="text-sm">
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="text-sm"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="default"
                className="text-sm"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>

        {/* Profile Picture */}
        <div className="flex items-center space-x-4 mb-6">
          <Avatar className="h-20 w-20 rounded-lg">
            <AvatarImage src={user?.Image} alt="Profile picture" />
            <AvatarFallback className="text-lg">{getInitials()}</AvatarFallback>
          </Avatar>
        </div>

        {/* User Details */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-5">
          {isEditing ? (
            <div className="flex flex-col gap-2">
              <Label className="ds-text-body-xs-medium text-ds-text-subtle">
                Full name
              </Label>
              <Input
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
                placeholder="Enter your full name"
              />
            </div>
          ) : (
            <SettingsField
              label="Full name"
              value={[user?.FirstName, user?.LastName]
                .filter(Boolean)
                .join(' ')}
            />
          )}

          {user?.Email && <SettingsField label="Email" value={user.Email} />}

          <SettingsField
            label={`${providerDisplayName} username`}
            value={
              user?.GitlabUsername ||
              user?.BitbucketUsername ||
              user?.GithubUsername
            }
            mono
          />

          <SettingsField label="Role" value={roleLabel(user?.Role)} />
        </div>
      </div>

      {/* Organization Details Section */}
      <Card>
        <CardHeader>
          <CardTitle className="ds-text-body-lg-semibold text-ds-text-darker">
            Organization Details
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <SettingsField label="Org name" value={organization?.OrgName} />
            <SettingsField label="Org ID" value={organization?.OrgUUID} mono />
            {linkedOrgs
              .filter((o) => o.orgUuid !== organization?.OrgUUID)
              .map((o) => (
                <SettingsFieldPair
                  key={o.orgUuid}
                  orgName={o.orgName}
                  orgUuid={o.orgUuid}
                />
              ))}
          </div>

          <Separator />

          <LinkedOrgsCard onLinkedOrgsChange={setLinkedOrgs} />

          {isAdmin && (
            <>
              <Separator />

              {/* Hide leaderboard org-wide (ENT-3531) */}
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <p className="ds-text-body-sm-semibold text-ds-text-darker">
                    Hide leaderboard
                  </p>
                  <p className="ds-text-body-sm-medium text-ds-text-subtle">
                    Hide the leaderboard from everyone in your organization.
                    When on, it’s removed from the sidebar, its page is
                    unavailable to all members, and it’s excluded from Slack and
                    email reports.
                  </p>
                </div>

                <Switch
                  checked={leaderboardHidden}
                  disabled={featureFlagsLoading || togglingLeaderboard}
                  onCheckedChange={handleLeaderboardHiddenToggle}
                  aria-label="Hide leaderboard for your organization"
                />
              </div>
            </>
          )}

          <Separator />

          {/* Leave organization */}
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="ds-text-body-sm-semibold text-ds-text-darker">
                Leave organization
              </p>
              <p className="ds-text-body-sm-medium text-ds-text-subtle">
                Remove yourself from this org. You will lose access to all
                organization resources and data.
              </p>
            </div>

            <Dialog>
              <DialogTrigger
                render={
                  <Button variant="destructive" className="shrink-0">
                    Leave Org
                  </Button>
                }
              />
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Leave Organization</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to leave this organization? This
                    action cannot be undone. You will lose access to all
                    organization resources and data.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" disabled={isLeaving}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleLeaveOrg}
                    disabled={isLeaving}
                    variant="destructive"
                  >
                    {isLeaving ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Leaving...
                      </div>
                    ) : (
                      'Leave Organization'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
